"use client";

/**
 * The product's tooltip: a dark bubble above the trigger, shown on
 * hover and on keyboard focus.
 *
 * It used to live inside ResultDashboard, which meant anything else
 * needing a tooltip fell back to the browser's `title` attribute - a
 * one-second delay and no styling, so the clearing-price line read as
 * having no explanation at all (owner, 2026-09-22: "the clearing
 * price, when I hover, doesn't have a tooltip").
 */
import type { ReactNode } from "react";

export default function HoverTip({
  text,
  align = "left",
  children,
}: {
  text: string;
  align?: "left" | "right";
  children: ReactNode;
}) {
  return (
    <span className="group relative inline-flex align-middle">
      <span tabIndex={0} aria-label={text} className="inline-flex cursor-help focus:outline-none">
        {children}
      </span>
      <span
        role="tooltip"
        // Background + foreground are inline hex on purpose:
        // ``bg-gray-900`` and ``text-white`` both ride the
        // ``.dark`` token swap in globals.css (gray-900 maps to
        // near-white, so the bubble disappeared against the dark
        // page while the text stayed white). Hardcoding the
        // values means the bubble is always a dark slate with
        // white text in both themes.
        style={{ backgroundColor: "rgb(17, 24, 39)", color: "rgb(255, 255, 255)" }}
        // Toggle DISPLAY (hidden → block), not opacity. An opacity-0
        // absolutely-positioned ``w-64`` bubble still occupies layout,
        // so on a narrow viewport its 256px box extends the document's
        // horizontal scroll width past the screen edge - that was the
        // ~27px right-side overflow on the result page at <380px.
        // display:none removes it from layout entirely while idle; it
        // still appears on hover/focus (we trade the 150ms fade for a
        // page that doesn't scroll sideways).
        className={`hidden group-hover:block group-focus-within:block pointer-events-none absolute ${align === "right" ? "right-0" : "left-0"} bottom-full mb-2 w-64 max-w-[calc(100vw-2rem)] rounded-lg px-3 py-2 text-[11px] font-normal normal-case tracking-normal leading-relaxed shadow-lg z-20`}
      >
        {text}
      </span>
    </span>
  );
}
